/**
 * LeaveService
 * Service for Leave entity operations
 * Route: api/leaves
 */
import { CrudService } from '../base/CrudService';
/**
 * LeaveService
 * Handles leave CRUD operations and leave-specific queries
 */
export class LeaveService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? LeaveService.DEFAULT_SERVICE_PATH,
            basePath: LeaveService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET MY LEAVE ====================
    /**
     * Get the current user's leave applications
     * GET api/leaves/my-leave
     */
    async getMyLeave() {
        const response = await this.getMyLeaveRaw();
        return response.data ?? [];
    }
    /**
     * Get the current user's leave applications with full response
     */
    getMyLeaveRaw() {
        const endpoint = this.getEndpoint('my-leave', 'my-leave');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET BY USER ID ====================
    /**
     * Get leave applications for a specific user
     * GET api/leaves/by-user/{userId}
     */
    async getByUserId(userId) {
        const response = await this.getByUserIdRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get leave applications for a specific user with full response
     */
    getByUserIdRaw(userId) {
        const endpoint = this.getEndpoint('by-user', `by-user/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET PENDING ====================
    /**
     * Get all pending leave applications
     * GET api/leaves/pending
     */
    async getPending() {
        const response = await this.getPendingRaw();
        return response.data ?? [];
    }
    /**
     * Get all pending leave applications with full response
     */
    getPendingRaw() {
        const endpoint = this.getEndpoint('pending', 'pending');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== GET BY STATUS ====================
    /**
     * Get leave applications by status
     * GET api/leaves/by-status/{statusId}
     */
    async getByStatus(statusId) {
        const response = await this.getByStatusRaw(statusId);
        return response.data ?? [];
    }
    /**
     * Get leave applications by status with full response
     */
    getByStatusRaw(statusId) {
        const endpoint = this.getEndpoint('by-status', `by-status/${statusId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== APPROVE ====================
    /**
     * Approve a leave application (HR only)
     * PUT api/leaves/{id}/approve
     */
    async approve(id, request) {
        const response = await this.approveRaw(id, request);
        if (!response.data) {
            throw new Error(`Failed to approve leave application ${id}`);
        }
        return response.data;
    }
    /**
     * Approve a leave application with full response
     */
    approveRaw(id, request) {
        const endpoint = this.getEndpoint('approve', `${id}/approve`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, request || {});
    }
    // ==================== REJECT ====================
    /**
     * Reject a leave application (HR only)
     * PUT api/leaves/{id}/reject
     */
    async reject(id, request) {
        const response = await this.rejectRaw(id, request);
        if (!response.data) {
            throw new Error(`Failed to reject leave application ${id}`);
        }
        return response.data;
    }
    /**
     * Reject a leave application with full response
     */
    rejectRaw(id, request) {
        const endpoint = this.getEndpoint('reject', `${id}/reject`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, request || {});
    }
    // ==================== CHANGE STATUS ====================
    /**
     * Change a leave application to any status — Pending / Approved / Rejected /
     * Deferred / Cancelled.
     * PUT api/leaves/{id}/status
     */
    async changeStatus(id, request) {
        const response = await this.changeStatusRaw(id, request);
        if (!response.data) {
            throw new Error(`Failed to change status of leave application ${id}`);
        }
        return response.data;
    }
    /**
     * Change a leave application's status with full response
     */
    changeStatusRaw(id, request) {
        const endpoint = this.getEndpoint('changeStatus', `${id}/status`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, request);
    }
    // ==================== DEFER / CANCEL / REOPEN ====================
    /** Defer a leave application — postpone the decision (HR only). PUT api/leaves/{id}/defer */
    async defer(id, request) {
        const response = await this.deferRaw(id, request);
        if (!response.data)
            throw new Error(`Failed to defer leave application ${id}`);
        return response.data;
    }
    deferRaw(id, request) {
        return this.http.put(this.buildUrl(this.getEndpoint('defer', `${id}/defer`)), request || {});
    }
    /** Cancel a leave application (HR only). PUT api/leaves/{id}/cancel */
    async cancel(id, request) {
        const response = await this.cancelRaw(id, request);
        if (!response.data)
            throw new Error(`Failed to cancel leave application ${id}`);
        return response.data;
    }
    cancelRaw(id, request) {
        return this.http.put(this.buildUrl(this.getEndpoint('cancel', `${id}/cancel`)), request || {});
    }
    /** Re-open a decided leave back to Pending (HR only). PUT api/leaves/{id}/reopen */
    async reopen(id) {
        const response = await this.reopenRaw(id);
        if (!response.data)
            throw new Error(`Failed to re-open leave application ${id}`);
        return response.data;
    }
    reopenRaw(id) {
        return this.http.put(this.buildUrl(this.getEndpoint('reopen', `${id}/reopen`)), {});
    }
    /** Adjust a leave's start/end dates (HR only). PUT api/leaves/{id}/dates */
    async adjustDates(id, request) {
        const response = await this.adjustDatesRaw(id, request);
        if (!response.data)
            throw new Error(`Failed to adjust dates of leave application ${id}`);
        return response.data;
    }
    adjustDatesRaw(id, request) {
        return this.http.put(this.buildUrl(this.getEndpoint('adjustDates', `${id}/dates`)), request);
    }
    // ==================== MODELVIEW ENDPOINTS ====================
    /**
     * Get leave ModelView by ID
     * GET api/leaves/view/{id}
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Leave ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get leave ModelView by ID with full response
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('view-by-id', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all leave ModelViews with pagination
     * GET api/leaves/view?page={page}&pageSize={pageSize}
     */
    async getAllViews(page = 1, pageSize = 50) {
        const response = await this.getAllViewsRaw(page, pageSize);
        if (!response.data) {
            throw new Error('Failed to retrieve leave ModelViews');
        }
        return response.data;
    }
    /**
     * Get all leave ModelViews with full response
     */
    getAllViewsRaw(page = 1, pageSize = 50) {
        const endpoint = this.getEndpoint('view-all', 'view');
        const url = this.buildUrlWithParams(endpoint, { page, pageSize });
        return this.http.get(url);
    }
    /**
     * Get leave ModelViews by user
     * GET api/leaves/view/by-user/{userId}
     */
    async getViewsByUser(userId) {
        const response = await this.getViewsByUserRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get leave ModelViews by user with full response
     */
    getViewsByUserRaw(userId) {
        const endpoint = this.getEndpoint('view-by-user', `view/by-user/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get leave ModelViews by status
     * GET api/leaves/view/by-status/{statusId}
     */
    async getViewsByStatus(statusId) {
        const response = await this.getViewsByStatusRaw(statusId);
        return response.data ?? [];
    }
    /**
     * Get leave ModelViews by status with full response
     */
    getViewsByStatusRaw(statusId) {
        const endpoint = this.getEndpoint('view-by-status', `view/by-status/${statusId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
LeaveService.DEFAULT_SERVICE_PATH = 'hr';
LeaveService.BASE_PATH = 'api/leaves';
//# sourceMappingURL=LeaveService.js.map